import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCcw, Check, Layers } from "lucide-react";
import { api } from "../lib/api";
import { missedMedIds } from "../lib/sr";
import { useProgress } from "../context/ProgressContext";
import { StaticStrip } from "../components/StaticStrip";

const TYPES = [
  { id: "all", label: "Both" },
  { id: "med", label: "Meds" },
  { id: "rhythm", label: "Rhythms" },
];

export default function Flashcards() {
  const { progress, update } = useProgress();
  const [meds, setMeds] = useState([]);
  const [rhythms, setRhythms] = useState([]);
  const [classes, setClasses] = useState({});
  const [scope, setScope] = useState("missed"); // missed | all
  const [type, setType] = useState("all");
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/medications"), api.get("/rhythms"), api.get("/medications/classes")]).then(
      ([m, r, c]) => {
        setMeds(m.data);
        setRhythms(r.data);
        setClasses(Object.fromEntries(c.data.map((x) => [x.id, x.name])));
      }
    );
  }, []);

  const missedMeds = useMemo(() => missedMedIds(progress?.medication), [progress?.medication]);
  const missedRhythms = useMemo(() => new Set(progress?.stats?.missed_rhythms || []), [progress?.stats?.missed_rhythms]);

  const deck = useMemo(() => {
    const m = meds
      .filter((x) => scope === "all" || missedMeds.has(x.id))
      .map((x) => ({ kind: "med", ...x }));
    const r = rhythms
      .filter((x) => scope === "all" || missedRhythms.has(x.id))
      .map((x) => ({ kind: "rhythm", ...x }));
    if (type === "med") return m;
    if (type === "rhythm") return r;
    return [...m, ...r];
  }, [meds, rhythms, scope, type, missedMeds, missedRhythms]);

  useEffect(() => { setIdx(0); setFlipped(false); }, [scope, type]);
  useEffect(() => { if (idx >= deck.length && deck.length) setIdx(deck.length - 1); }, [deck.length, idx]);

  const card = deck[idx];
  const go = (d) => { setFlipped(false); setIdx((i) => (i + d + deck.length) % deck.length); };

  const markLearned = () => {
    if (!card) return;
    if (card.kind === "med") {
      const medication = { ...(progress?.medication || {}) };
      const cls = { ...(medication[card.class] || { weights: {}, mastery: {} }) };
      cls.weights = { ...(cls.weights || {}), [card.id]: 0.8 };
      medication[card.class] = cls;
      update({ medication });
    } else {
      const stats = progress?.stats || {};
      update({ stats: { ...stats, missed_rhythms: (stats.missed_rhythms || []).filter((id) => id !== card.id) } });
    }
    setFlipped(false);
  };

  const missedCount = missedMeds.size + missedRhythms.size;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground mb-6" data-testid="back-dashboard">
        <ArrowLeft className="w-3 h-3" /> BACK
      </Link>

      <div className="mb-8">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// STUDY MODE</div>
        <h1 className="text-4xl font-extrabold tracking-tight">
          Quiet <span className="text-phosphor">flashcards</span>
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          No timer, no points. Flip through what you missed, read the explanation, and mark it learned when it sticks.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex border border-border">
          <button
            onClick={() => setScope("missed")}
            data-testid="scope-missed"
            className={`px-4 py-2 text-xs tracking-widest ${scope === "missed" ? "bg-primary/10 text-phosphor" : "text-muted-foreground hover:text-foreground"}`}
          >
            MISSED ({missedCount})
          </button>
          <button
            onClick={() => setScope("all")}
            data-testid="scope-all"
            className={`px-4 py-2 text-xs tracking-widest border-l border-border ${scope === "all" ? "bg-primary/10 text-phosphor" : "text-muted-foreground hover:text-foreground"}`}
          >
            ALL ({meds.length + rhythms.length})
          </button>
        </div>
        <div className="flex border border-border">
          {TYPES.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              data-testid={`type-${t.id}`}
              className={`px-4 py-2 text-xs tracking-widest ${i ? "border-l border-border" : ""} ${type === t.id ? "bg-primary/10 text-phosphor" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="ml-auto text-xs text-muted-foreground tracking-widest" data-testid="deck-counter">
          {deck.length ? `${idx + 1} / ${deck.length}` : "0 / 0"}
        </div>
      </div>

      {!card ? (
        <div className="panel p-12 text-center" data-testid="deck-empty">
          <Layers className="w-10 h-10 text-phosphor mx-auto mb-4" />
          <div className="text-lg font-bold">Nothing to review here.</div>
          <div className="text-sm text-muted-foreground mt-2">
            {scope === "missed" ? "You have no missed items in this deck — switch to ALL to browse everything." : "Deck is loading…"}
          </div>
        </div>
      ) : (
        <>
          <button
            onClick={() => setFlipped((f) => !f)}
            data-testid="flashcard"
            className="panel scanlines w-full text-left p-8 min-h-[340px] relative transition-colors hover:border-primary/50"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="text-[10px] tracking-[0.3em] text-muted-foreground">
                {card.kind === "med" ? `MEDICATION · ${classes[card.class] || card.class}` : `RHYTHM · LEVEL ${card.level}`}
              </span>
              <span className={`text-[10px] tracking-[0.3em] ${flipped ? "text-phosphor" : "text-muted-foreground"}`}>
                {flipped ? "ANSWER" : "TAP TO FLIP"}
              </span>
            </div>

            {!flipped ? (
              <div data-testid="flashcard-front">
                {card.kind === "med" ? (
                  <div className="text-xl sm:text-2xl leading-snug">{card.clue}</div>
                ) : (
                  <>
                    <StaticStrip params={card.params} />
                    <div className="mt-4 text-sm text-muted-foreground">Identify this rhythm.</div>
                  </>
                )}
              </div>
            ) : (
              <div data-testid="flashcard-back">
                <div className="text-3xl font-extrabold text-phosphor capitalize">{card.kind === "med" ? card.generic : card.name}</div>
                {card.kind === "med" ? (
                  <>
                    <div className="mt-2 text-xs tracking-widest text-muted-foreground">
                      ALSO: {card.aliases.filter((a) => a !== card.generic).join(" · ") || "—"}
                    </div>
                    <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{card.clue}</p>
                  </>
                ) : (
                  <>
                    <div className="mt-2 text-xs tracking-widest text-muted-foreground">
                      ALSO: {card.aliases.slice(1).join(" · ")}
                      {card.lethal && <span className="text-destructive ml-3">● LETHAL</span>}
                    </div>
                    <p className="mt-5 text-sm text-muted-foreground leading-relaxed">{card.explanation}</p>
                  </>
                )}
              </div>
            )}
          </button>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={() => go(-1)} data-testid="card-prev" className="border border-phosphor/40 px-4 py-3 hover:bg-primary/10">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => go(1)} data-testid="card-next" className="border border-phosphor/40 px-4 py-3 hover:bg-primary/10">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => setFlipped((f) => !f)} data-testid="card-flip" className="inline-flex items-center gap-2 border border-phosphor/40 px-5 py-3 text-xs tracking-widest hover:bg-primary/10">
              <RotateCcw className="w-3.5 h-3.5" /> FLIP
            </button>
            {(card.kind === "med" ? missedMeds.has(card.id) : missedRhythms.has(card.id)) && (
              <button onClick={markLearned} data-testid="card-learned" className="ml-auto inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-xs tracking-widest font-semibold hover:bg-primary/90">
                <Check className="w-3.5 h-3.5" /> MARK LEARNED
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
